import { Facebook, Instagram, Twitter, Youtube, Linkedin, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export const SocialIconsPreview = ({ settings }: any) => {
    const iconMap: Record<string, any> = {
        facebook: Facebook,
        instagram: Instagram,
        twitter: Twitter,
        youtube: Youtube,
        linkedin: Linkedin,
        email: Mail,
    };

    const sizeClasses: any = {
        sm: "w-8 h-8",
        md: "w-10 h-10",
        lg: "w-12 h-12",
    };

    const styleClasses: any = {
        rounded: "rounded-lg",
        square: "rounded-none",
        circle: "rounded-full",
    };

    const template = settings.template || "default";

    return (
        <div className="flex justify-center gap-3 p-4">
            {(settings.platforms || ["facebook", "instagram", "twitter"]).map((platform: string) => {
                const Icon = iconMap[platform];
                if (!Icon) return null;

                let bgClass = "";
                let textClass = "";
                let borderClass = "";

                if (template === "outline") {
                    bgClass = "bg-transparent";
                    textClass = settings.color === "brand" ? "text-gray-800" : "text-gray-600";
                    borderClass = "border-2 border-current";
                } else if (template === "minimal") {
                    bgClass = "bg-transparent";
                    textClass = settings.color === "brand" ? "text-gray-800" : "text-gray-600";
                    borderClass = "";
                } else {
                    // Default
                    bgClass = settings.color === "brand" ? "bg-gray-800" : "bg-gray-100";
                    textClass = settings.color === "brand" ? "text-white" : "text-gray-600";
                    borderClass = "";
                }

                return (
                    <button
                        key={platform}
                        className={cn(
                            "flex items-center justify-center transition-all hover:scale-110",
                            sizeClasses[settings.size || "md"],
                            styleClasses[settings.style || "rounded"],
                            bgClass,
                            textClass,
                            borderClass
                        )}
                    >
                        <Icon className="w-5 h-5" />
                    </button>
                );
            })}
        </div>
    );
};

export const StickyBarPreview = ({ settings }: any) => {
    const template = settings.template || "standard";

    const containerClasses = cn(
        "fixed left-0 right-0 z-50 transition-all duration-300",
        settings.position === "top" ? "top-0" : "bottom-0",
        template === "floating_card" ? "p-4" : ""
    );

    const innerClasses = cn(
        "flex items-center justify-between gap-4",
        template === "floating_card" ? "bg-white rounded-xl shadow-2xl p-4 max-w-2xl mx-auto border" : "container mx-auto px-4 py-3",
        template === "minimal" ? "py-2" : ""
    );

    const barStyle = template === "floating_card" ? {
        backgroundColor: settings.backgroundColor || "#ffffff",
        color: settings.textColor || "#000000",
    } : {
        backgroundColor: settings.backgroundColor || "#000000",
        color: settings.textColor || "#ffffff",
    };

    return (
        <div className={containerClasses} style={template !== "floating_card" ? barStyle : {}}>
            <div className={innerClasses} style={template === "floating_card" ? barStyle : {}}>
                {/* Product Info */}
                <div className="flex items-center gap-3 flex-1">
                    {settings.showImage && (
                        <div className={cn(
                            "bg-white/10 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0",
                            template === "minimal" ? "w-8 h-8" : "w-12 h-12"
                        )}>
                            {settings.imageUrl ? (
                                <img src={settings.imageUrl} alt="Product" className="w-full h-full object-cover" />
                            ) : (
                                <span className={template === "minimal" ? "text-lg" : "text-2xl"}>📦</span>
                            )}
                        </div>
                    )}
                    <div className="flex-1 min-w-0">
                        <div className="font-bold text-sm truncate">{settings.productName || "Product Name"}</div>
                        {template !== "minimal" && (
                            <div className="text-lg font-bold">
                                {settings.currency || "$"}{settings.price || "99.99"}
                            </div>
                        )}
                    </div>
                </div>

                {/* CTA Button */}
                <Button
                    className="flex-shrink-0 font-bold shadow-lg"
                    size={template === "minimal" ? "sm" : "default"}
                    style={{
                        backgroundColor: settings.buttonColor || "#F59E0B",
                        color: "#000000",
                    }}
                >
                    {settings.buttonText || "Order Now"} 🛒
                </Button>
            </div>
        </div>
    );
};
